import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { preventiviAPI, driveAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import DriveBrowser from '../components/DriveBrowser';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  FileText,
  FileDown,
  Loader2,
  Pencil,
  ExternalLink,
  Check,
  FolderOpen,
} from 'lucide-react';

const DettaglioPreventivo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [preventivo, setPreventivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Document generation
  const [templateBrowserOpen, setTemplateBrowserOpen] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  
  // Drive settings (template and folder from settings)
  const [driveSettings, setDriveSettings] = useState(null);

  useEffect(() => {
    fetchPreventivo();
    checkDriveStatus();
  }, [id]);

  const fetchPreventivo = async () => {
    try {
      const response = await preventiviAPI.get(id);
      setPreventivo(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del preventivo');
      navigate('/preventivi');
    } finally {
      setLoading(false);
    }
  };

  const checkDriveStatus = async () => {
    try {
      const response = await driveAPI.status();
      setDriveConnected(response.data.connected);
      
      if (response.data.connected) {
        // Load drive settings to get default template
        const settingsResponse = await driveAPI.getSettings();
        setDriveSettings(settingsResponse.data);
        if (settingsResponse.data.template_id) {
          setSelectedTemplate({
            id: settingsResponse.data.template_id,
            name: settingsResponse.data.template_name
          });
        }
      }
    } catch (error) {
      console.error('Error checking drive status:', error);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleUpdateStato = async (newStato) => {
    setUpdating(true);
    try {
      await preventiviAPI.updateStato(id, newStato);
      setPreventivo({ ...preventivo, stato: newStato });
      toast.success(`Stato aggiornato a "${getStatusLabel(newStato)}"`);
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenGenerateDialog = () => {
    if (!driveConnected) {
      toast.error('Connetti prima Google Drive nelle Impostazioni');
      return;
    }
    setTemplateBrowserOpen(true);
  };

  const handleGenerateDocument = async () => {
    if (!selectedTemplate?.id) {
      toast.error('Seleziona un template');
      return;
    }

    setGenerating(true);
    try {
      const response = await preventiviAPI.generaDocumento(id, selectedTemplate.id);
      toast.success('Documento generato con successo!');
      setPreventivo({
        ...preventivo,
        documento_id: response.data.document_id,
        documento_url: response.data.document_url,
      });
      setTemplateBrowserOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const response = format === 'pdf' 
        ? await preventiviAPI.downloadPdf(id)
        : await preventiviAPI.downloadDocx(id);
      
      const blob = new Blob([response.data], {
        type: format === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Preventivo_${preventivo.codice}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Download ${format.toUpperCase()} completato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il download');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  if (!preventivo) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="dettaglio-preventivo-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/preventivi')}
              className="h-10 w-10 p-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-heading font-bold text-slate-900">
                  {preventivo.codice}
                </h1>
                <Badge variant="outline" className={getStatusColor(preventivo.stato)}>
                  {getStatusLabel(preventivo.stato)}
                </Badge>
              </div>
              <p className="text-slate-500 mt-1">{preventivo.cliente_ragione_sociale}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {preventivo.documento_url ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="download-dropdown">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() => handleDownload('pdf')}
                    disabled={downloading === 'pdf'}
                  >
                    {downloading === 'pdf' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4 mr-2" />
                    )}
                    Scarica PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDownload('docx')}
                    disabled={downloading === 'docx'}
                  >
                    {downloading === 'docx' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Scarica DOCX
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={preventivo.documento_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Apri in Drive
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="outline"
                onClick={handleOpenGenerateDialog}
                data-testid="generate-document-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Genera Documento
              </Button>
            )}
            <Link to={`/preventivi/${id}/modifica`}>
              <Button variant="outline">
                <Pencil className="w-4 h-4 mr-2" />
                Modifica
              </Button>
            </Link>
          </div>
        </div>

        {/* Status Update */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label className="whitespace-nowrap">Aggiorna Stato:</Label>
              <Select
                value={preventivo.stato}
                onValueChange={handleUpdateStato}
                disabled={updating}
              >
                <SelectTrigger className="w-48" data-testid="stato-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bozza">Bozza</SelectItem>
                  <SelectItem value="inviato">Inviato</SelectItem>
                  <SelectItem value="accettato">Accettato</SelectItem>
                  <SelectItem value="rifiutato">Rifiutato</SelectItem>
                </SelectContent>
              </Select>
              {updating && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Dati Preventivo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-500">Codice</span>
                <span className="font-mono font-medium">{preventivo.codice}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Data</span>
                <span>{formatDate(preventivo.data)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Validità</span>
                <span>{preventivo.validita_giorni} giorni</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Creato il</span>
                <span>{formatDate(preventivo.created_at)}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Cliente</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-slate-900">{preventivo.cliente_ragione_sociale}</p>
            </CardContent>
          </Card>
        </div>

        {/* Servizi */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Servizi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {preventivo.servizi.map((servizio, index) => (
                <div key={index} className="p-5 border border-slate-200 rounded-lg bg-white">
                  {/* Header con titolo e prezzi */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 pb-4 border-b border-slate-100">
                    <h4 className="text-lg font-semibold text-slate-900">{servizio.nome}</h4>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {servizio.prezzo_listino !== servizio.prezzo_scontato && (
                        <span className="text-sm text-slate-400 line-through">
                          {formatCurrency(servizio.prezzo_listino)}
                        </span>
                      )}
                      <span className="text-lg font-bold text-orange-600 bg-orange-50 px-3 py-1 rounded-lg">
                        {formatCurrency(servizio.prezzo_scontato)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Descrizione */}
                  <div 
                    className="text-slate-600 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: servizio.descrizione }}
                  />
                  
                  {/* Attività Previste */}
                  {servizio.attivita_previste && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm font-semibold text-slate-800 mb-2">Attività Previste per questa offerta:</p>
                      <div 
                        className="text-slate-600 prose prose-sm max-w-none bg-slate-50 p-4 rounded-lg"
                        dangerouslySetInnerHTML={{ __html: servizio.attivita_previste }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-8 p-6 bg-slate-50 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between text-slate-600">
                  <span>Totale Listino</span>
                  <span className="font-medium">{formatCurrency(preventivo.totale_listino)}</span>
                </div>
                {preventivo.sconto > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Sconto applicato</span>
                    <span className="font-medium">-{formatCurrency(preventivo.sconto)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold pt-3 border-t border-slate-200">
                  <span>Totale</span>
                  <span className="text-orange-600">{formatCurrency(preventivo.totale_scontato)} + IVA</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        {(preventivo.note || preventivo.modalita_pagamento) && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Note e Condizioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {preventivo.modalita_pagamento && (
                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-2">Modalità di Pagamento</p>
                  <div 
                    className="text-slate-600 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: preventivo.modalita_pagamento }}
                  />
                </div>
              )}
              {preventivo.note && (
                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-2">Note</p>
                  <div 
                    className="text-slate-600 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: preventivo.note }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Generate Document Dialog */}
        <Dialog open={templateBrowserOpen} onOpenChange={setTemplateBrowserOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Genera Documento</DialogTitle>
              <DialogDescription>
                Seleziona un template per generare il preventivo
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Template</Label>
                {selectedTemplate ? (
                  <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-sky-50">
                    <FileText className="w-5 h-5 text-sky-600" />
                    <span className="flex-1 truncate font-medium">{selectedTemplate.name}</span>
                    <Check className="w-4 h-4 text-sky-600" />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 border border-dashed border-slate-300 rounded-lg text-slate-500">
                    <FileText className="w-5 h-5" />
                    <span>Nessun template selezionato</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setTemplateBrowserOpen(false);
                    setTimeout(() => {
                      setSelectedTemplate(null);
                      setTemplateBrowserOpen(true);
                    }, 100);
                  }}
                >
                  {selectedTemplate ? 'Cambia template' : 'Seleziona template'}
                </Button>
              </div>

              {/* Destination folder info */}
              {driveSettings?.folder_name && (
                <div className="space-y-2">
                  <Label>Salva in</Label>
                  <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                    <FolderOpen className="w-5 h-5 text-amber-500" />
                    <span className="flex-1 truncate">{driveSettings.folder_name}</span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTemplateBrowserOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleGenerateDocument}
                disabled={!selectedTemplate || generating}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="confirm-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generazione...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Genera
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DriveBrowser for template selection - opens when no template selected */}
        <DriveBrowser
          open={templateBrowserOpen && !selectedTemplate}
          onOpenChange={(open) => {
            if (!open && !selectedTemplate) {
              setTemplateBrowserOpen(false);
            }
          }}
          onSelect={(template) => {
            setSelectedTemplate(template);
          }}
          mode="template"
          title="Seleziona Template"
          description="Naviga nel tuo Drive e seleziona un Google Doc come template"
        />
      </div>
    </Layout>
  );
};

export default DettaglioPreventivo;
